import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Sun, Moon, Menu, X, Search } from 'lucide-react';
import { useColorMode } from '../../contexts/ColorModeContext';

interface NavbarProps {
  onOpenCmdk?: () => void;
}

const links: { to: string; label: string }[] = [
  { to: '/services', label: 'Services' },
  { to: '/about-us', label: 'About' },
  { to: '/blog', label: 'Blog' },
  { to: '/contact', label: 'Contact' },
];

const Navbar: React.FC<NavbarProps> = ({ onOpenCmdk }) => {
  const { mode, toggle } = useColorMode();
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Focus trap + Escape for mobile menu
  useEffect(() => {
    if (!mobileOpen) return;
    const el = menuRef.current;
    if (!el) return;

    const focusable = Array.from(el.querySelectorAll<HTMLElement>('a, button'));
    focusable[0]?.focus();

    const trap = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last?.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };

    document.addEventListener('keydown', trap);
    return () => document.removeEventListener('keydown', trap);
  }, [mobileOpen]);

  return (
    <header className="navbar">
      <div className="navbar__inner">
        <Link to="/" className="navbar__logo" aria-label="ConsultSRE home">
          <img
            className="navbar__logo-mark"
            src={mode === 'light' ? '/brand/consultsre-mark-light.svg' : '/brand/consultsre-mark-dark.svg'}
            alt=""
            aria-hidden="true"
            width={28}
            height={28}
          />
          <span>
            Consult<span style={{ color: 'var(--accent)' }}>SRE</span>
            <span className="brand-caret" aria-hidden="true" />
          </span>
        </Link>

        <nav className="navbar__links" aria-label="Primary">
          {links.map(l => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              className={({ isActive }) => (isActive ? 'active' : undefined)}
            >
              {({ isActive }) => (
                <span aria-current={isActive ? 'page' : undefined}>{l.label}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <span className="navbar__spacer" />

        <button
          type="button"
          className="navbar__cmdk"
          onClick={onOpenCmdk}
          aria-label="Open command palette"
        >
          <span>Search services…</span>
          <kbd>⌘K</kbd>
        </button>

        <button
          type="button"
          className="navbar__icon-btn navbar__cmdk-mobile"
          onClick={onOpenCmdk}
          aria-label="Open command palette"
        >
          <Search size={16} />
        </button>

        <button
          type="button"
          className="navbar__icon-btn"
          onClick={toggle}
          aria-label={`Switch to ${mode === 'dark' ? 'light' : 'dark'} mode`}
          title={`Switch to ${mode === 'dark' ? 'light' : 'dark'} mode`}
        >
          {mode === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
        </button>

        <Link to="/contact" className="navbar__cta">
          Book an assessment
          <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth={1.5}>
            <path d="M2 6h8M7 3l3 3-3 3" />
          </svg>
        </Link>

        <button
          type="button"
          className="navbar__burger"
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen(v => !v)}
        >
          {mobileOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <div
        ref={menuRef}
        className={`navbar__mobile ${mobileOpen ? 'open' : ''}`}
        aria-hidden={!mobileOpen}
      >
        {links.map(l => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.to === '/'}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) => (isActive ? 'active' : undefined)}
            tabIndex={mobileOpen ? 0 : -1}
          >
            {l.label}
          </NavLink>
        ))}
        <div className="navbar__mobile-divider" aria-hidden="true" />
        <button
          type="button"
          className="navbar__mobile-action"
          tabIndex={mobileOpen ? 0 : -1}
          onClick={() => {
            setMobileOpen(false);
            toggle();
          }}
        >
          {mode === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
          Switch to {mode === 'dark' ? 'light' : 'dark'} mode
        </button>
        <button
          type="button"
          className="navbar__mobile-action"
          tabIndex={mobileOpen ? 0 : -1}
          onClick={() => {
            setMobileOpen(false);
            onOpenCmdk?.();
          }}
        >
          <Search size={15} />
          Search (⌘K)
        </button>
      </div>
    </header>
  );
};

export default Navbar;
